import PropertiesEditor from '/src/components/propertiesEditor';
import googleMapsApi from '@api/googleMapsApi';
import locationApi from '@api/locationApi';
import AddressInput from '@components/addressInput';
import ItemEditor from '@components/itemEditor';
import OrgMultiSelect from '@components/orgMultiSelect';
import OrgSelect from '@components/orgSelect';
import OrgUserList from '@components/orgUserList';
import useAuth from '@hooks/useAuth';
import { useTranslator } from '@i18n';
import { Group, Spoiler, Stack, TextInput } from '@mantine/core';
import type { Location } from '@models/location';
import type { Org } from '@models/org';
import AreaList from '@routes/profile/locations/-areaList';
import { createFileRoute } from '@tanstack/react-router';
import { LoadingBoundary, createForm } from 'cross-state/react';

export const Route = createFileRoute('/profile/locations/$id')({
  component: LocationEditor,
});

const locationForm = createForm<Location>({
  defaultValue: {
    id: '',
    name: '',
    address: {
      street: '',
      zip: '',
      city: '',
    },
    parentOrgId: '',
    additionalOrgIds: [],
    areas: [],
  },
  validations: {
    name: { required: (x) => !!x },
    parentOrgId: { required: (x) => !!x },
  },
});

function LocationEditor() {
  const t = useTranslator();
  const { hasClaim } = useAuth();
  const { id: locationId } = Route.useParams();
  const isNew = locationId === 'new';
  const readonly = isNew ? !hasClaim('locations/create', 'any') : !hasClaim('locations/write', locationId);
  const [location] = locationApi.get(locationId).useCache({ disabled: isNew });
  // The backend checks delete in the owning organization only, not in the location's groups
  const deletable = !!location && hasClaim('locations/delete', location.parentOrgId || 'global');

  return (
    <ItemEditor
      domain="locations"
      api={locationApi}
      readonly={readonly}
      deletable={deletable}
      form={locationForm}
      name={(location) => location.name}
      footer={
        <div
          css={{
            marginTop: 'var(--mantine-spacing-xl)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--mantine-spacing-md)',
          }}
        >
          {!isNew && <AreaList locationId={locationId} />}

          <LoadingBoundary>{!isNew && <OrgUserList type="location" orgId={locationId} />}</LoadingBoundary>
        </div>
      }
      prepareDraft={async (draft, original) => {
        if (draft.address) {
          const { hash, ...payload } = draft.address;
          const before = original?.address;
          const addressChanged =
            payload.street !== before?.street || payload.zip !== before?.zip || payload.city !== before?.city;
          const addressEmpty = !payload.street?.trim() && !payload.zip?.trim() && !payload.city?.trim();

          if (addressChanged && addressEmpty) {
            payload.placeId = undefined;
            payload.lat = undefined;
            payload.lng = undefined;
          } else if (addressChanged && (!hash || hash !== JSON.stringify(payload))) {
            const result = await googleMapsApi.addressToPosition(payload).get();
            if (!result) {
              throw new Error(t('locations.addressNotFound'));
            }

            payload.placeId = result.place_id;
            payload.lat = result.geometry.location.lat();
            payload.lng = result.geometry.location.lng();
          }

          draft.address = payload;
        }

        return draft;
      }}
    >
      <LoadingBoundary>
        <locationForm.Field
          name="parentOrgId"
          render={(props, _field, form) => (
            <OrgSelect
              {...props}
              onChange={(value) => {
                if (value === props.value) {
                  return;
                }

                props.onChange(value);
                // Groups belong to a single tenant, so whatever was picked no longer applies
                form.getField('additionalOrgIds').setValue([]);
              }}
              label={t('locations.properties.parentOrg.label')}
              description={t('locations.properties.parentOrg.description')}
              withAsterisk
              readOnly={!isNew}
              filterOptions={(org: Org) =>
                readonly ? true : org.type === 'tenant' && hasClaim('locations/create', org.id)
              }
              searchable
              autoComplete="off"
              spellCheck={false}
              placeholder={t('common.select')}
            />
          )}
        />

        <locationForm.FormState selector={(state) => state.draft.parentOrgId}>
          {(parentOrgId) => (
            <locationForm.Field
              name="additionalOrgIds"
              render={(props) => (
                <OrgMultiSelect
                  {...props}
                  label={t('locations.properties.additionalOrgs.label')}
                  description={t('locations.properties.additionalOrgs.description')}
                  icon="group"
                  // Only groups of the owning organization, and only once there is one
                  disabled={!parentOrgId}
                  filterOptions={(org: Org) =>
                    org.type === 'group' &&
                    org.parentOrgId === parentOrgId &&
                    (readonly || hasClaim('locations/write', org.id))
                  }
                  clearable
                  searchable
                  type="search"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={t('common.select')}
                />
              )}
            />
          )}
        </locationForm.FormState>
      </LoadingBoundary>

      <locationForm.Field
        name="name"
        render={(props) => (
          <TextInput
            {...props}
            label={t('locations.properties.name.label')}
            withAsterisk
            autoComplete="off"
            spellCheck={false}
          />
        )}
      />

      <locationForm.Field
        name="address.street"
        render={(props, _field, form) => (
          <AddressInput
            {...props}
            onAddressChange={(address) => {
              form.getField('address').setValue(address);
            }}
            label={t('locations.properties.street.label')}
            autoComplete="street-address"
            spellCheck={false}
          />
        )}
      />

      <Group>
        <locationForm.Field
          name="address.zip"
          render={(props) => (
            <TextInput
              {...props}
              label={t('locations.properties.zip.label')}
              autoComplete="postal-code"
              spellCheck={false}
              css={{
                flex: 1,
              }}
            />
          )}
        />

        <locationForm.Field
          name="address.city"
          render={(props) => (
            <TextInput
              {...props}
              label={t('locations.properties.city.label')}
              autoComplete="address-level2"
              spellCheck={false}
              css={{
                flex: 3,
              }}
            />
          )}
        />
      </Group>

      <Spoiler
        showLabel={<>+ {t('external.show')}</>}
        hideLabel={<>- {t('external.hide')}</>}
        maxHeight={0}
        mt="md"
        css={{
          '--mantine-color-anchor': 'currentColor',
        }}
      >
        <Stack mb="md">
          <locationForm.Field
            name="externalReference"
            render={(props) => (
              <TextInput
                {...props}
                value={props.value ?? ''}
                label={t('external.externalReference.label')}
                description={t('external.externalReference.description')}
                autoComplete="off"
                spellCheck={false}
              />
            )}
          />

          <locationForm.Field name="externalProperties" render={(props) => <PropertiesEditor {...props} />} />
        </Stack>
      </Spoiler>
    </ItemEditor>
  );
}
